<template>
  <div class="automated-email-sent-ui">
    <VFallbackText v-if="isLoading" class="centered">Loading…</VFallbackText>

    <template v-else>
      <div class="automated-email-sent-ui__filters">
        <div class="automated-email-sent-ui__filters-search">
          <VInput
            v-model="filterContactName"
            :config="{
              label: '',
              name: 'contact-name',
              placeholder: 'Search by contact name…',
              inputMode: 'search',
            }"
          />
        </div>

        <div class="automated-email-sent-ui__filters-group">
          <span class="automated-email-sent-ui__filters-label">Trigger</span>
          <div class="automated-email-sent-ui__filters-dropdown">
            <VDropdown
              :model-value="filterTriggerName"
              :options="TRIGGER_DROPDOWN_OPTIONS"
              @update:model-value="setFilterTriggerName"
            />
          </div>
        </div>

        <div class="automated-email-sent-ui__filters-group">
          <span class="automated-email-sent-ui__filters-label">Mode</span>
          <div class="automated-email-sent-ui__filters-dropdown">
            <VDropdown
              :model-value="filterMode"
              :options="MODE_DROPDOWN_OPTIONS"
              @update:model-value="setFilterMode"
            />
          </div>
        </div>

        <div class="automated-email-sent-ui__filters-group automated-email-sent-ui__filters-group--dates">
          <label class="automated-email-sent-ui__date-field">
            <span class="automated-email-sent-ui__date-label">From</span>
            <input v-model="filterSentAtFrom" class="automated-email-sent-ui__date-input" type="date" />
          </label>
          <label class="automated-email-sent-ui__date-field">
            <span class="automated-email-sent-ui__date-label">To</span>
            <input v-model="filterSentAtTo" class="automated-email-sent-ui__date-input" type="date" />
          </label>
        </div>

      </div>

      <VFallbackText v-if="!hasDisplayEmails" class="auto-email-outbox">
        {{
          shouldShowEmptyListHint
            ? 'No sent emails match the current filters.'
            : 'No sent emails yet. Emails will appear here once they have been sent.'
        }}
      </VFallbackText>

      <template v-else>
        <div class="automated-email-sent-ui__table" @click="handleTableRowClick">
          <div class="automated-email-sent-ui__table-header">
            <VTableHeader class="auto-email-sent" :titles="TABLE_HEADERS">
              <template #col-0>
                <VCheckbox :model-value="allSelected" @change="toggleSelectAll" />
              </template>
            </VTableHeader>
          </div>
          <VTable :table="table" class="auto-email-sent">
            <template #col-0="{ colData }">
              <div class="auto-email-sent__checkbox" @click.stop>
                <VCheckbox :model-value="isSelected(colData)" @change="toggleSelection(colData)" />
              </div>
            </template>

            <template #col-2="{ colData }">
              <span
                :class="[
                  'automated-email-sent-ui__trigger-badge',
                  `automated-email-sent-ui__trigger-badge--${triggerVariant(colData)}`,
                ]"
              >
                {{ colData }}
              </span>
            </template>

            <template #col-3="{ colData }">
              <div class="automated-email-sent-ui__subject-cell">
                <span class="automated-email-sent-ui__subject">{{ colData }}</span>
              </div>
            </template>

            <template #col-4="{ colData }">
              <div class="automated-email-sent-ui__subject-cell">
                <span class="automated-email-sent-ui__subject">{{ colData }}</span>
              </div>
            </template>

            <template #col-5="{ colData }">
              <span
                :class="[
                  'automated-email-sent-ui__mode-badge',
                  `automated-email-sent-ui__mode-badge--${modeVariant(colData)}`,
                ]"
              >
                {{ colData }}
              </span>
            </template>

            <template #no-data-message>
              <VFallbackText class="auto-email-outbox">
                No sent emails yet. Emails will appear here once they have been sent.
              </VFallbackText>
            </template>
          </VTable>
        </div>

        <div v-if="pagination" class="automated-email-sent-ui__pagination">
          <VPagination :pagination="pagination" @on-go-to-page="fetchSent" />
        </div>
      </template>
    </template>
  </div>
</template>

<script lang="ts" setup>
  // Types
  import type { ComputedRef } from 'vue';
  import type { ITable, TTableData } from '~/misc/v-table/types';
  import type { TSentEmail } from '~/modules/opportunities/automated-email/types';
  import type { TAutomatedEmailSentFilters } from '~/modules/opportunities/automated-email/sent/composables/use-sent-filters/types';

  // Config
  import {
    TABLE_HEADERS,
    TRIGGER_DROPDOWN_OPTIONS,
    MODE_DROPDOWN_OPTIONS,
    CONTACT_NAME_FILTER_DEBOUNCE_IN_MS,
  } from '~/modules/opportunities/automated-email/sent/config';

  // Services
  import { AutomatedEmailTriggerVariantService } from '~/modules/opportunities/automated-email/services/trigger-variant';

  // Composables
  import { useAutomatedEmailSentFilters } from '~/modules/opportunities/automated-email/sent/composables/use-sent-filters';
  import { useSent } from '~/modules/opportunities/automated-email/sent/composables/use-sent';
  import useModal from '~/composables/use-modal';

  // Components
  import VFallbackText from '~/misc/v-fallback-text/VFallbackText.vue';
  import VCheckbox from '~/misc/v-checkbox';
  import VInput from '~/misc/v-input';
  import VDropdown from '~/misc/v-dropdown';
  import VTable from '~/misc/v-table';
  import VTableHeader from '~/misc/v-table-header/VTableHeader.vue';
  import VPagination from '~/misc/v-pagination/VPagination.vue';

  // Utils
  import { debounce } from 'lodash-es';

  const SentEmailModal = defineAsyncComponent(
    () => import('~/modules/opportunities/automated-email/sent/sent-email-modal/SentEmailModal.vue')
  );

  const filters: TAutomatedEmailSentFilters = useAutomatedEmailSentFilters();

  const {
    patchId,
    saleType,
    filterContactName,
    filterTriggerName,
    filterMode,
    filterSentAtFrom,
    filterSentAtTo,
    setFilterTriggerName,
    setFilterMode,
  } = filters;

  const {
    emails,
    pagination,
    isLoading,
    allSelected,
    isSelected,
    toggleSelection,
    toggleSelectAll,
    fetchSent,
  } = useSent(filters);

  const { show: showModal } = useModal();

  const hasDisplayEmails: ComputedRef<boolean> = computed<boolean>(
    () => emails.value.length > 0
  );

  const shouldShowEmptyListHint: ComputedRef<boolean> = computed<boolean>(
    () =>
      filterTriggerName.value !== null ||
      filterContactName.value.trim() !== '' ||
      filterMode.value !== null ||
      filterSentAtFrom.value !== '' ||
      filterSentAtTo.value !== ''
  );

  const tableData: ComputedRef<TTableData> = computed<TTableData>(() =>
    emails.value.map((email: TSentEmail) => [
      email.id,
      email.contact_name,
      email.trigger,
      email.subject,
      email.date_sent,
      email.mode,
    ])
  );

  const table: ComputedRef<ITable> = computed<ITable>(() => ({
    headers: [],
    data: tableData.value,
  }));

  const triggerVariant = (trigger: string) =>
    AutomatedEmailTriggerVariantService.resolve(trigger);

  const modeVariant = (mode: string): string => {
    return mode.toLowerCase().includes('fully') ? 'fully' : 'semi';
  };

  const handleTableRowClick = (event: MouseEvent): void => {
    const target: HTMLElement = event.target as HTMLElement;
    const row: Element | null = target.closest('.v-table-ui__row');

    if (!row) {
      return;
    }

    const rows: Element[] = Array.from(
      row.parentElement?.querySelectorAll('.v-table-ui__row') ?? []
    );
    const rowIndex: number = rows.indexOf(row) - 1;

    if (rowIndex < 0 || rowIndex >= emails.value.length) {
      return;
    }

    const email: TSentEmail = emails.value[rowIndex];

    openEmailModal(email);
  };

  const openEmailModal = (email: TSentEmail): void => {
    showModal(SentEmailModal, { email });
  };

  const refetchFirstPage = (): void => {
    fetchSent(1).catch(() => undefined);
  };

  const debouncedRefetchFirstPage = debounce(refetchFirstPage, CONTACT_NAME_FILTER_DEBOUNCE_IN_MS);

  watch(filterContactName, debouncedRefetchFirstPage);

  watch(
    [filterTriggerName, filterMode, filterSentAtFrom, filterSentAtTo],
    refetchFirstPage
  );

  watch(
    [patchId, saleType],
    ([nextPatchId, nextSaleType], [previousPatchId, previousSaleType]) => {
      if (
        nextPatchId === undefined ||
        (nextPatchId === previousPatchId && nextSaleType === previousSaleType)
      ) {
        return;
      }

      refetchFirstPage();
    }
  );

  onMounted(() => {
    if (patchId.value !== undefined) {
      fetchSent();
    }
  });
</script>
